import { getEnvironment } from 'utils';
import { PENDO_ENVIRONMENT_CONFIG } from 'features/pendo/constants';
import { setupAssessmentsPendoScript } from './assessmentsPendoScript';

export interface AssessmentsPendoIds {
  tenantId?: string | null;
  surveyEventId?: string | null;
}

// The assessments agent is a separate Pendo app, so member-web's agent must stay off these routes
export const isAssessmentsPendoRoute = (pathname?: string) =>
  (pathname ?? window.location.pathname).split('/').includes('assessments');

// typeof: the global does not exist at all until the script stub is injected
const isPendoLoaded = () => typeof pendo !== 'undefined' && !!pendo;

const getAssessmentsPendoApiKey = () => process.env.REACT_APP_ASSESSMENTS_PENDO_KEY;

const shouldInitialize = (apiKey?: string) => {
  const env = getEnvironment();
  const isInvalidEnv = env === 'local' || env === 'auto' || env === 'dev';
  if (isInvalidEnv) {
    console.warn('Assessments Pendo initialization skipped: Invalid environment', env);
    return false;
  }
  if (!apiKey) {
    console.warn('Assessments Pendo initialization skipped: Missing Pendo key');
    return false;
  }

  return true;
};

// Exported for testing purposes
export function getAssessmentsPendoConfig(tenantId: string, surveyEventId: string, apiKey: string) {
  let pendoEnv: string = PENDO_ENVIRONMENT_CONFIG[getEnvironment()];

  if (pendoEnv === 'DEV') {
    const personalEnvName = window.location.host.split('.')[0];
    pendoEnv = `DEV-${personalEnvName.toUpperCase()}`;
  }

  return {
    apiKey,
    visitor: { id: `${pendoEnv}_BROWSER_${surveyEventId}` },
    account: { id: `${pendoEnv}_BROWSER_${tenantId}` },
  };
}

export async function loadAssessmentsPendoScript() {
  try {
    const apiKey = getAssessmentsPendoApiKey();
    if (!apiKey || isPendoLoaded()) return;

    await setupAssessmentsPendoScript(apiKey);
  } catch (error) {
    console.error('Error loading Assessments Pendo script:', error);
  }
}

export const isAssessmentsPendoReady = () =>
  isPendoLoaded() && !(pendo as any).isAnonymousVisitor?.();

export const trackAssessmentsPendoEvent = (
  eventName: string,
  eventData: Record<string, string>,
) => {
  try {
    pendo.track(eventName, eventData);
  } catch (error) {
    console.error('Error tracking Assessments Pendo event', error);
  }
};

export async function initializeAssessmentsPendo({ tenantId, surveyEventId }: AssessmentsPendoIds) {
  try {
    const apiKey = getAssessmentsPendoApiKey();
    if (!shouldInitialize(apiKey)) return;

    if (!tenantId || !surveyEventId) {
      console.warn('Assessments Pendo initialization skipped: Missing tenantId or surveyEventId');
      return;
    }

    if (!isPendoLoaded()) {
      await setupAssessmentsPendoScript(apiKey as string);
    }

    const pendoConfig = getAssessmentsPendoConfig(tenantId, surveyEventId, apiKey as string);

    if (pendo.isReady?.()) {
      pendo.identify(pendoConfig);
    } else {
      pendo.initialize(pendoConfig);
    }
  } catch (error) {
    // Not propagating the error to avoid handling error in the component
    console.error('Error initializing Assessments Pendo:', error);
  }
}
